import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useOverridesContext, usePackageOptionsContext } from '../../PricingToolSessionContext';
import { PRICING_OPTION_LABELS } from '../../constants';
import type { PricingOptionIndex } from '../../types';

import { isOverrideSetUntouched, validateOverrides } from './overridesValidation';
import type { OverrideBasis } from './types';

const BASIS_OPTIONS: Array<{ value: OverrideBasis; label: string }> = [
  { value: 'vendor_revenue', label: 'Vendor revenue' },
  { value: 'room_nights', label: 'Room nights' },
];

const PERCENT_ADORNMENT = { endAdornment: <InputAdornment position="end">%</InputAdornment> };

const TIER_COLUMNS = [
  { label: 'Name *', width: '28%' },
  { label: 'From', width: '16%' },
  { label: 'To *', width: '16%' },
  { label: 'Comms *', width: '16%' },
  { label: 'Tax adjustments', width: '18%' },
];

function toNumber(value: string): number {
  if (value === '') {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function OverridesTab() {
  const { activeOverrides, setBasis, addTier, updateTier, removeTier } = useOverridesContext();
  const { activeOption, setActiveOption } = usePackageOptionsContext();
  const errors = validateOverrides(activeOverrides);
  const nameRequired = !isOverrideSetUntouched(activeOverrides);

  return (
    <TabPanelShell subtitle="Options left on their defaults are not saved." title="Overrides">
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box>
            <Typography gutterBottom variant="subtitle2">
              Option
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={activeOption}
              onChange={(_, next: PricingOptionIndex | null) => next !== null && setActiveOption(next)}
            >
              {PRICING_OPTION_LABELS.map((label, index) => (
                <ToggleButton key={label} value={index}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography gutterBottom variant="subtitle2">
              Basis
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={activeOverrides.basis}
              onChange={(_, value: OverrideBasis | null) => value && setBasis(value)}
            >
              {BASIS_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Stack>

        {errors.length > 0 && (
          <Alert severity="error" variant="outlined">
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </Box>
          </Alert>
        )}

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {TIER_COLUMNS.map((column) => (
                  <TableCell key={column.label} sx={{ width: column.width, whiteSpace: 'nowrap' }}>
                    {column.label}
                  </TableCell>
                ))}
                <TableCell sx={{ width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {activeOverrides.tiers.map((tier, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      error={nameRequired && !tier.name?.trim()}
                      fullWidth
                      inputProps={{ 'aria-label': `Tier ${index + 1} name` }}
                      onChange={(event) => updateTier(index, { name: event.target.value || undefined })}
                      placeholder="Tier name"
                      required
                      size="small"
                      value={tier.name ?? ''}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      inputProps={{ 'aria-label': `Tier ${index + 1} from` }}
                      onChange={(event) =>
                        updateTier(index, {
                          from: event.target.value === '' ? undefined : toNumber(event.target.value),
                        })
                      }
                      placeholder="Optional"
                      size="small"
                      type="number"
                      value={tier.from ?? ''}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      inputProps={{ 'aria-label': `Tier ${index + 1} to` }}
                      onChange={(event) =>
                        updateTier(index, {
                          to: event.target.value === '' ? undefined : toNumber(event.target.value),
                        })
                      }
                      size="small"
                      type="number"
                      value={tier.to ?? ''}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      InputProps={PERCENT_ADORNMENT}
                      fullWidth
                      inputProps={{ 'aria-label': `Tier ${index + 1} comms` }}
                      onChange={(event) =>
                        updateTier(index, {
                          comms: event.target.value === '' ? undefined : toNumber(event.target.value),
                        })
                      }
                      size="small"
                      type="number"
                      value={tier.comms ?? ''}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      InputProps={PERCENT_ADORNMENT}
                      fullWidth
                      inputProps={{ 'aria-label': `Tier ${index + 1} tax adjustments` }}
                      onChange={(event) =>
                        updateTier(index, {
                          taxAdjustmentPct: event.target.value === '' ? undefined : toNumber(event.target.value),
                        })
                      }
                      placeholder="Optional"
                      size="small"
                      type="number"
                      value={tier.taxAdjustmentPct ?? ''}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton aria-label={`Remove tier ${index + 1}`} onClick={() => removeTier(index)} size="small">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box>
          <Button onClick={addTier} size="small" startIcon={<AddIcon />} variant="outlined">
            Add tier
          </Button>
        </Box>
      </Stack>
    </TabPanelShell>
  );
}
